// GoaliesView — two goalies, each a solid dome + blocking shield arc +
// save-volume ring decal. Mode/pulled state lands in per-team uniforms;
// transforms are mutated in place.

import { GOALIE_SAVE_RADIUS, type MatchSnapshot } from "@arcade-hockey/sim";
import { Group, Mesh } from "three/webgpu";
import {
  buildDecalQuadGeometry,
  buildDomeGeometry,
  buildShieldGeometry,
} from "../geometry/entity-geometry.js";
import {
  type GoalieUniforms,
  applyGoalieStyle,
  createGoalieMaterials,
} from "../materials/goalie.js";
import { type EntitiesStyle, entitiesStyle } from "../style/style-schema.js";
import { simToWorldInto, worldYawFromSimDir } from "../world-mapping.js";

type GoalieModule = typeof import("../materials/goalie.js");

const SHIELD_HALF_ANGLE = Math.PI / 3;

type GoalieParts = {
  group: Group;
  dome: Mesh;
  shield: Mesh;
  saveRing: Mesh;
  u: GoalieUniforms;
};

export class GoaliesView {
  readonly root: Group;
  private readonly byTeam: Record<"home" | "away", GoalieParts>;

  constructor() {
    this.root = new Group();
    this.byTeam = {
      home: this.buildGoalie("home"),
      away: this.buildGoalie("away"),
    };
    activeGoaliesView = this;
  }

  private buildGoalie(team: "home" | "away"): GoalieParts {
    const mats = createGoalieMaterials(entitiesStyle, team);
    const group = new Group();
    const bodyR = GOALIE_SAVE_RADIUS * entitiesStyle.goalie.bodyRadiusFrac;

    const dome = new Mesh(buildDomeGeometry(), mats.dome);
    dome.scale.set(bodyR, bodyR * 0.5, bodyR);
    group.add(dome);

    const shield = new Mesh(buildShieldGeometry(SHIELD_HALF_ANGLE), mats.shield);
    shield.scale.setScalar(bodyR * 1.45);
    shield.position.y = 0.12;
    group.add(shield);

    const saveRing = new Mesh(buildDecalQuadGeometry(), mats.saveRing);
    saveRing.scale.setScalar(GOALIE_SAVE_RADIUS);
    saveRing.position.y = 0.012;
    saveRing.renderOrder = 1;
    group.add(saveRing);

    this.root.add(group);
    return { group, dome, shield, saveRing, u: mats.u };
  }

  applyStyle(s: EntitiesStyle): void {
    applyGoalieStyle(this.byTeam.home.u, s);
    applyGoalieStyle(this.byTeam.away.u, s);
  }

  hotSwap(mod: GoalieModule): void {
    for (const team of ["home", "away"] as const) {
      const parts = this.byTeam[team];
      const mats = mod.createGoalieMaterials(entitiesStyle, team);
      for (const [mesh, mat] of [
        [parts.dome, mats.dome],
        [parts.shield, mats.shield],
        [parts.saveRing, mats.saveRing],
      ] as const) {
        const old = mesh.material as { dispose(): void };
        mesh.material = mat;
        old.dispose();
      }
      parts.u = mats.u;
    }
  }

  update(snap: MatchSnapshot): void {
    const s = entitiesStyle;
    for (const g of snap.goalies) {
      const parts = this.byTeam[g.teamId];
      simToWorldInto(parts.group.position, g.pos);
      parts.shield.rotation.y = worldYawFromSimDir(g.facing);
      parts.shield.visible = !g.pulled;
      parts.saveRing.visible = !g.pulled;
      parts.u.dim.value = g.pulled ? s.goalie.pulledDim : 1;
      parts.u.saveRingAlpha.value =
        g.mode === "Saving"
          ? s.goalie.saveRingAlphaSaving
          : g.mode === "Recovering"
            ? s.goalie.saveRingAlphaRecovering
            : s.goalie.saveRingAlphaReady;
    }
  }
}

let activeGoaliesView: GoaliesView | null = null;

if (import.meta.hot) {
  import.meta.hot.accept("../materials/goalie.ts", (m) => {
    if (m !== undefined) activeGoaliesView?.hotSwap(m as unknown as GoalieModule);
  });
}
